import { isNumber } from 'lodash'
import { NAWSurvey } from '../../../../../types/NAW/Survey'

// CONSTANTS

const maxHomeofficeDays = 5
const einzelarbeitSquareMeter = 8
const kommunikationSquareMeter = 15
const ablageSquareMeter = 0.375

const garderobeSquareMeter = 0.036
const lockerSquareMeter = 0.075
const druckerSquareMeter = 0.15
const workCafeSquareMeter = 0.75
const konferenzbereichSquareMeter = 0.3

const verkehrsflaechePercentage = 15

// HELPER FUNCTIONS

const getAnwesenheitPercentage = (survey: NAWSurvey): number => {
  return 100 - (100 / maxHomeofficeDays) * (survey.homeoffice || 0)
}

const getHomeofficePercentage = (survey: NAWSurvey): number => {
  return 100 - (100 / maxHomeofficeDays) * (survey.homeoffice || 0)
}
const getEinzelarbeitPercentage = (survey: NAWSurvey): number => {
  return 100 - (survey.collaborationPercentage || 0)
}

const getKommunikationPercentage = (survey: NAWSurvey): number => {
  return survey.collaborationPercentage || 0
}

const getDigitalisierungPercentage = (survey: NAWSurvey): number => {
  return 100 - (survey.digitalPercentage || 0)
}

export const getMitarbeiter = (survey: NAWSurvey): number => {
  return survey.mitarbeiter || 0
}

const getAblageSquareMeter = (survey: NAWSurvey): number => {
  return (getMitarbeiter(survey) / 100) * getDigitalisierungPercentage(survey) * ablageSquareMeter
}

const getFleacheIst = (survey: NAWSurvey): number => {
  return survey.flaecheIst || 0
}

const getPricePerSquareMeter = (survey: NAWSurvey): number => {
  return survey.pricePerSquareMeter || 0
}

const getEinzelarbeitFlaeche = (survey: NAWSurvey): number => {
  const einzel = getMitarbeiter(survey) * survey.deskSharingPercentage * einzelarbeitSquareMeter
  return einzel
}

const getKommunikationFlaeche = (survey: NAWSurvey): number => {
  const maAnwesend = (getMitarbeiter(survey) / 100) * getHomeofficePercentage(survey)
  const kommunikationAnwesend = (maAnwesend / 100) * getKommunikationPercentage(survey)
  const kommunikationFleache = kommunikationAnwesend * kommunikationSquareMeter
  return kommunikationFleache
}

const getUebergeordneteBedarfeFleache = (survey: NAWSurvey): number => {
  const garderobeFleache = getMitarbeiter(survey) * garderobeSquareMeter
  const lockerFleache = getMitarbeiter(survey) * lockerSquareMeter
  const druckerFleache = getMitarbeiter(survey) * druckerSquareMeter
  const workCafeFleache = getMitarbeiter(survey) * workCafeSquareMeter
  const konferenzbereichFleache = getMitarbeiter(survey) * konferenzbereichSquareMeter
  return garderobeFleache + lockerFleache + druckerFleache + workCafeFleache + konferenzbereichFleache
}

const getVerkehrsflaeche = (survey: NAWSurvey): number => {
  const einzelarbeitFleache = getEinzelarbeitFlaeche(survey)
  const kommunikationFleache = getKommunikationFlaeche(survey)
  const ablageFleache = getAblageSquareMeter(survey)
  const uebergeordneteBedarfeFleache = getUebergeordneteBedarfeFleache(survey)
  const totalFleache = einzelarbeitFleache + kommunikationFleache + ablageFleache + uebergeordneteBedarfeFleache

  return Math.ceil((totalFleache / 100) * verkehrsflaechePercentage)
}

const getCurrentImmobilienPrice = (survey: NAWSurvey): number => {
  return getFleacheIst(survey) * getPricePerSquareMeter(survey)
}

export const getNewImmobilienPrice = (survey: NAWSurvey): number => {
  return getNeueBueroflaeche(survey) * getPricePerSquareMeter(survey)
}

// MAIN FUNCTIONS

export const getNeueBueroflaeche = (survey: NAWSurvey) => {
  const einzelarbeitFleache = getEinzelarbeitFlaeche(survey)
  const kommunikationFleache = getKommunikationFlaeche(survey)
  const ablageFleache = getAblageSquareMeter(survey)
  const uebergeordneteBedarfeFleache = getUebergeordneteBedarfeFleache(survey)
  const verkehrsflaeche = getVerkehrsflaeche(survey)

  return Math.ceil(einzelarbeitFleache + kommunikationFleache + ablageFleache + uebergeordneteBedarfeFleache + verkehrsflaeche)
}

export const getErsparnisPercentage = (survey: NAWSurvey): string | number => {
  if (survey.flaecheIst < getNeueBueroflaeche(survey)) {
    return 0
    // return `- ${Math.ceil(100 - (100 / getNeueBueroflaeche(survey)) * survey.flaecheIst)}`
  }
  return Math.ceil(100 - (100 / survey.flaecheIst) * getNeueBueroflaeche(survey))
}

export const getJaehrlicheErsparnis = (survey: NAWSurvey): number => {
  const result = getCurrentImmobilienPrice(survey) - getNewImmobilienPrice(survey)
  if (result < 0) {
    return 0
  }
  return getCurrentImmobilienPrice(survey) - getNewImmobilienPrice(survey)
}

export const getRecommendedDeskSharingQuote = (survey: NAWSurvey): number => {
  return (
    Math.round(
      ((((getEinzelarbeitPercentage(survey) + getKommunikationPercentage(survey) * 0.5) / 100) * getAnwesenheitPercentage(survey)) / 100) * 100,
    ) / 100
  )
}

export const getTables = (survey: NAWSurvey, quote?: number) => {
  if (isNumber(quote)) {
    return Math.ceil(getMitarbeiter(survey) * quote)
  }
  return Math.ceil(getMitarbeiter(survey) * survey.deskSharingPercentage)
}
